import { createFileRoute, redirect } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  UserCircle,
  Building2,
  Mail,
  Lock,
  Loader2,
  CheckCircle2,
  Save,
  AlertTriangle,
  Camera,
  Eye,
  EyeOff,
  ShieldCheck,
  Sparkles,
  Check,
  Code2,
  Palette,
  Trophy
} from "lucide-react";
import { PREFERENCE_CATEGORIES } from "@/lib/preferences";
import { cn } from "@/lib/utils";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger,
  DialogFooter,
  DialogDescription
} from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";

export const Route = createFileRoute("/_student/settings")({
  head: () => ({
    meta: [
      { title: "Profile Settings — WeFest" },
      { name: "description", content: "Manage your student profile, college affiliation, and preferences." },
    ],
  }),
  beforeLoad: async ({ location }) => {
    if (typeof window === 'undefined') return;
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw redirect({
        to: '/login',
        search: { redirect: location.pathname + location.searchStr },

      });
    }
  },
  component: StudentSettings,
});

const CATEGORY_COLORS: Record<string, { bg: string; border: string; text: string }> = {
  technical: {
    bg: "bg-blue-500/10",
    border: "border-blue-500/20",
    text: "text-blue-400",
  },
  cultural: {
    bg: "bg-purple-500/10",
    border: "border-purple-500/20",
    text: "text-purple-400",
  },
  sports: {
    bg: "bg-emerald-500/10",
    border: "border-emerald-500/20",
    text: "text-emerald-400",
  },
};

const CATEGORY_ICONS: Record<string, React.ElementType> = {
  technical: Code2,
  cultural: Palette,
  sports: Trophy,
};

function StudentSettings() {
  const queryClient = useQueryClient();

  // Fetch current user
  const { data: user, isLoading: loadingUser } = useQuery({
    queryKey: ["current-user"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      return user;
    },
  });

  // Fetch student profile
  const { data: profile, isLoading: loadingProfile } = useQuery({
    queryKey: ["student-profile", user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      const { data } = await supabase
        .from("student_profiles")
        .select(`*, colleges (id, name, city)`)
        .eq("id", user!.id)
        .maybeSingle();

      if (!data) {
        // Auto-create profile if missing
        const { data: newProfile } = await supabase
          .from("student_profiles")
          .insert({
            id: user!.id,
            full_name: user!.user_metadata?.full_name || "",
            college_id: user!.user_metadata?.college_id || null,
          })
          .select()
          .single();
        return { ...newProfile, colleges: null };
      }
      return data;
    },
  });

  // Fetch all approved colleges for selection
  const { data: colleges, isLoading: loadingColleges } = useQuery({
    queryKey: ["all-colleges"],
    queryFn: async () => {
      const { data } = await supabase
        .from("colleges")
        .select("id, name, city")
        .eq("status", "approved")
        .order("name");
      return data || [];
    },
  });

  // Form State
  const [fullName, setFullName] = useState("");
  const [bio, setBio] = useState("");
  const [collegeId, setCollegeId] = useState<string>("none");
  const [isPublic, setIsPublic] = useState(true);
  const [selectedInterests, setSelectedInterests] = useState<string[]>([]);
  const [isDirty, setIsDirty] = useState(false);

  // Password Change State
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPasswords, setShowPasswords] = useState(false);

  const getPasswordStrength = (pass: string) => {
    let strength = 0;
    if (pass.length >= 6) strength += 20;
    if (pass.length >= 10) strength += 20;
    if (/[A-Z]/.test(pass)) strength += 20;
    if (/[0-9]/.test(pass)) strength += 20;
    if (/[^A-Za-z0-9]/.test(pass)) strength += 20;
    return strength;
  };

  const strength = getPasswordStrength(newPassword);
  const strengthColor = strength <= 40 ? "bg-red-500" : strength <= 80 ? "bg-amber-500" : "bg-emerald-500";
  const strengthText = strength <= 40 ? "Weak" : strength <= 80 ? "Medium" : "Strong";

  useEffect(() => {
    if (profile) {
      setFullName(profile.full_name || user?.user_metadata?.full_name || "");
      setBio(profile.bio || "");
      setCollegeId(profile.college_id || user?.user_metadata?.college_id || "none");
      setIsPublic(profile.is_public ?? true);
      setSelectedInterests(profile.interests || []);
    }
  }, [profile, user]);

  const updateProfileMutation = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error("Not logged in");
      
      const { error } = await supabase
        .from("student_profiles")
        .update({
          full_name: fullName,
          bio,
          college_id: collegeId === "none" ? null : collegeId,
          is_public: isPublic,
          interests: selectedInterests,
        })
        .eq("id", user.id);
        
      if (error) throw error;

      // Keep Auth metadata in sync
      await supabase.auth.updateUser({
        data: { full_name: fullName },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["student-profile"] });
      queryClient.invalidateQueries({ queryKey: ["current-user"] });
      toast.success("Profile updated successfully");
      setIsDirty(false);
    },
    onError: (e: any) => toast.error(e.message),
  });

  const changePasswordMutation = useMutation({
    mutationFn: async () => {
      if (!user?.email) throw new Error("User email not found");
      if (newPassword !== confirmPassword) throw new Error("New passwords do not match");
      if (newPassword.length < 6) throw new Error("Password must be at least 6 characters");
      if (oldPassword === newPassword) throw new Error("New password must be different from old password");

      // Verify old password
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: oldPassword,
      });

      if (signInError) throw new Error("Incorrect current password");

      // Update password
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (updateError) throw updateError;
    },
    onSuccess: () => {
      toast.success("Password updated successfully");
      setIsPasswordModalOpen(false);
      setOldPassword("");
      setNewPassword("");
      setConfirmPassword("");
    },
    onError: (e: any) => toast.error(e.message),
  });

  if (loadingUser || loadingProfile || loadingColleges) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const initials = fullName.substring(0, 2).toUpperCase() || user?.email?.substring(0, 2).toUpperCase() || "ST";

  return (
    <div className="container mx-auto max-w-4xl px-6 py-10 animate-in fade-in slide-in-from-bottom-4 duration-700">
      <div className="mb-8">
        <h1 className="font-display text-3xl font-black tracking-tight">Profile & Settings</h1>
        <p className="text-muted-foreground mt-1 font-medium">Manage your public presence and college affiliation.</p>
      </div>

      <div className="grid gap-8 md:grid-cols-[1fr_300px]">
        {/* Main Settings Form */}
        <div className="space-y-6">
          <section className="glass-panel rounded-3xl p-6 md:p-8 border-white/5 relative overflow-hidden">
            <div className="absolute top-0 right-0 h-32 w-32 bg-primary/5 rounded-full blur-[50px]" />
            
            <div className="flex items-center gap-3 mb-6 relative z-10">
              <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary shadow-inner border border-primary/20">
                <UserCircle className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-lg font-bold">Personal Information</h2>
                <p className="text-xs text-muted-foreground">This info will be visible to other students.</p>
              </div>
            </div>

            <form 
              onSubmit={(e) => { 
                e.preventDefault(); 
                updateProfileMutation.mutate(); 
              }} 
              className="space-y-5 relative z-10"
            >
              <div className="flex flex-col sm:flex-row gap-6">
                <div className="relative group shrink-0">
                  <div className="h-24 w-24 rounded-2xl bg-brand-gradient p-0.5 shadow-glow ring-4 ring-background">
                    <div className="h-full w-full rounded-[14px] bg-background flex items-center justify-center">
                      <span className="text-3xl font-black text-primary">{initials}</span>
                    </div>
                  </div>
                  <div className="absolute inset-0 rounded-2xl bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer backdrop-blur-sm">
                    <Camera className="h-6 w-6 text-white" />
                  </div>
                </div>

                <div className="flex-1 space-y-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Full Name</Label>
                    <Input 
                      value={fullName} 
                      onChange={(e) => { setFullName(e.target.value); setIsDirty(true); }}
                      className="h-12 rounded-xl bg-background/50 border-border/50 text-base font-medium"
                      placeholder="Your name"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-1.5 pt-2">
                <Label className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Bio</Label>
                <Textarea 
                  value={bio}
                  onChange={(e) => { setBio(e.target.value); setIsDirty(true); }}
                  placeholder="Tell the community about yourself... (e.g., Computer Science sophomore, avid photographer)"
                  className="min-h-[100px] resize-none rounded-xl bg-background/50 border-border/50"
                />
              </div>

              <div className="space-y-1.5 pt-2">
                <Label className="text-xs font-bold text-muted-foreground uppercase tracking-wider">College Affiliation</Label>
                {collegeId !== "none" && collegeId !== null ? (
                  <div className="h-12 rounded-xl bg-background/50 border border-border/50 font-medium flex items-center px-4 text-muted-foreground">
                    <Building2 className="h-4 w-4 mr-2 text-primary" />
                    {(profile?.colleges as any)?.name || "Linked College"}
                  </div>
                ) : (
                  <Select 
                    value={collegeId} 
                    onValueChange={(v) => { setCollegeId(v); setIsDirty(true); }}
                  >
                    <SelectTrigger className="h-12 rounded-xl bg-background/50 border-border/50 font-medium">
                      <SelectValue placeholder="Select your college" />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      <SelectItem value="none">Not affiliated / Other</SelectItem>
                      {colleges?.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.name} {c.city && <span className="text-muted-foreground ml-1">({c.city})</span>}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <p className="text-[10px] text-muted-foreground mt-1">
                  {collegeId !== "none" 
                    ? "Your college affiliation is locked. Contact support to change it." 
                    : "Linking your college unlocks campus-specific events and leaderboards."}
                </p>
              </div>

              <div className="flex items-center justify-between rounded-xl border border-border/40 bg-background/30 p-4 mt-6">
                <div className="space-y-0.5">
                  <Label className="text-sm font-bold">Public Profile</Label>
                  <p className="text-xs text-muted-foreground">Allow other users to see your profile and digital memories.</p>
                </div>
                <Switch 
                  checked={isPublic} 
                  onCheckedChange={(v) => { setIsPublic(v); setIsDirty(true); }} 
                />
              </div>

              {/* Interests & Preferences Section */}
              <div className="space-y-4 pt-4 border-t border-border/20">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label className="text-sm font-bold">Interests & Preferences</Label>
                    <p className="text-xs text-muted-foreground">Select your interests to personalize your events discovery.</p>
                  </div>
                  {selectedInterests.length > 0 && (
                    <span className="text-[10px] font-black text-primary bg-primary/10 border border-primary/20 px-2 py-0.5 rounded-full uppercase tracking-widest">
                      {selectedInterests.length} Selected
                    </span>
                  )}
                </div>

                <div className="space-y-5 mt-2">
                  {PREFERENCE_CATEGORIES.map((cat) => {
                    const Icon = CATEGORY_ICONS[cat.id] || Sparkles;
                    const colors = CATEGORY_COLORS[cat.id] || CATEGORY_COLORS.technical;
                    
                    return (
                      <div key={cat.id} className="space-y-2">
                        <div className="flex items-center gap-1.5 text-xs font-bold text-muted-foreground uppercase tracking-wider">
                          <Icon className="h-3.5 w-3.5" />
                          <span>{cat.label}</span>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {cat.interests.map((interest) => {
                            const isSelected = selectedInterests.includes(interest);
                            return (
                              <button
                                key={interest}
                                type="button"
                                onClick={() => {
                                  setSelectedInterests((prev) => {
                                    const next = isSelected
                                      ? prev.filter((i) => i !== interest)
                                      : [...prev, interest];
                                    return next;
                                  });
                                  setIsDirty(true);
                                }}
                                className={cn(
                                  "flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold transition-all duration-200 border",
                                  isSelected
                                    ? `${colors.bg} ${colors.border} ${colors.text} ring-1 ring-white/5`
                                    : "bg-white/[0.02] border-white/5 text-muted-foreground hover:bg-white/5 hover:text-foreground"
                                )}
                              >
                                {isSelected && <Check className="h-3 w-3 shrink-0" />}
                                {interest}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="flex items-center justify-between pt-6 border-t border-border/20">
                {isDirty ? (
                  <span className="text-xs font-bold text-amber-500 flex items-center gap-1.5">
                    <AlertTriangle className="h-4 w-4" /> Unsaved changes
                  </span>
                ) : (
                  <span className="text-xs font-bold text-muted-foreground flex items-center gap-1.5">
                    <CheckCircle2 className="h-4 w-4 text-emerald-500" /> Up to date
                  </span>
                )}
                <Button 
                  type="submit" 
                  disabled={!isDirty || updateProfileMutation.isPending}
                  className="bg-brand-gradient text-white rounded-xl font-bold px-6 shadow-glow"
                >
                  {updateProfileMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                  Save Changes
                </Button>
              </div>
            </form>
          </section>
        </div>

        {/* Sidebar Info */}
        <div className="space-y-4">
          <div className="glass-panel rounded-2xl p-5 border-white/5">
            <div className="flex items-center gap-2.5 mb-4">
              <Lock className="h-5 w-5 text-muted-foreground" />
              <h3 className="font-bold text-sm">Account Security</h3>
            </div>
            <div className="space-y-4">
              <div>
                <Label className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">Email Address</Label>
                <div className="flex items-center gap-2 mt-1 px-3 py-2 rounded-lg bg-background/50 border border-border/40">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium truncate">{user?.email}</span>
                </div>
              </div>
              <div>
                <Label className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">Password</Label>
                <Dialog open={isPasswordModalOpen} onOpenChange={setIsPasswordModalOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" className="w-full mt-1 justify-start font-medium text-xs rounded-lg h-9 hover:bg-primary/5 hover:text-primary transition-colors">
                      <Lock className="h-3 w-3 mr-2" />
                      Change Password
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-[400px] glass-panel border-white/10 bg-zinc-950/90 backdrop-blur-xl p-0 overflow-hidden">
                    <div className="absolute top-0 right-0 h-32 w-32 bg-primary/10 rounded-full blur-[50px] -mr-16 -mt-16" />
                    
                    <DialogHeader className="p-6 pb-0 relative z-10">
                      <DialogTitle className="text-xl font-black flex items-center gap-2">
                        <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary border border-primary/20">
                          <ShieldCheck className="h-4 w-4" />
                        </div>
                        Update Password
                      </DialogTitle>
                      <DialogDescription className="text-xs text-muted-foreground mt-1">
                        Secure your account by choosing a strong, unique password.
                      </DialogDescription>
                    </DialogHeader>

                    <div className="p-6 space-y-4 relative z-10">
                      <div className="space-y-1.5">
                        <Label className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">Current Password</Label>
                        <div className="relative">
                          <Input
                            type={showPasswords ? "text" : "password"}
                            value={oldPassword}
                            onChange={(e) => setOldPassword(e.target.value)}
                            placeholder="••••••••"
                            className="h-11 bg-background/50 border-white/5 rounded-xl pr-10 focus:ring-primary/20"
                          />
                        </div>
                      </div>

                      <div className="space-y-1.5">
                        <div className="flex justify-between items-center">
                          <Label className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">New Password</Label>
                          {newPassword && (
                            <span className={`text-[10px] font-bold uppercase tracking-widest ${strengthColor.replace('bg-', 'text-')}`}>
                              {strengthText}
                            </span>
                          )}
                        </div>
                        <div className="relative">
                          <Input
                            type={showPasswords ? "text" : "password"}
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            placeholder="••••••••"
                            className="h-11 bg-background/50 border-white/5 rounded-xl pr-10 focus:ring-primary/20"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPasswords(!showPasswords)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-white transition-colors"
                          >
                            {showPasswords ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </div>
                        {newPassword && (
                          <div className="space-y-1">
                            <Progress value={strength} className="h-1 bg-white/5" indicatorClassName={strengthColor} />
                          </div>
                        )}
                      </div>

                      <div className="space-y-1.5">
                        <Label className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">Confirm New Password</Label>
                        <Input
                          type={showPasswords ? "text" : "password"}
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          placeholder="••••••••"
                          className="h-11 bg-background/50 border-white/5 rounded-xl focus:ring-primary/20"
                        />
                      </div>
                    </div>

                    <DialogFooter className="p-6 bg-white/[0.02] border-t border-white/5 relative z-10">
                      <Button
                        variant="ghost"
                        onClick={() => setIsPasswordModalOpen(false)}
                        className="rounded-xl font-bold text-xs"
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={() => changePasswordMutation.mutate()}
                        disabled={changePasswordMutation.isPending || !oldPassword || !newPassword || !confirmPassword}
                        className="bg-brand-gradient text-white rounded-xl font-bold px-6 shadow-glow"
                      >
                        {changePasswordMutation.isPending ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          "Update Password"
                        )}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
          </div>

          <div className="glass-panel rounded-2xl p-5 border-white/5 border-primary/20 bg-primary/5">
            <div className="flex items-center gap-2.5 mb-2">
              <Building2 className="h-5 w-5 text-primary" />
              <h3 className="font-bold text-sm text-primary">College Status</h3>
            </div>
            {collegeId !== "none" ? (
              <p className="text-xs text-muted-foreground leading-relaxed">
                You are currently affiliated with <span className="text-primary font-bold">{(profile?.colleges as any)?.name || "your college"}</span>. Your fest points contribute to your college's overall ranking in the National Leaderboard.
              </p>
            ) : (
              <p className="text-xs text-muted-foreground leading-relaxed">
                You are not currently linked to any college. Select your institution to join the campus network and represent them!
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
